/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SplineChart from '../../components/Charts/SplineChart';
import { Filters } from './Filters';
import { EmptyState, Modal, Skeleton } from '../../components/ui';
import { operations, periods } from '../../constants';
import { sendRequest } from '../../utils/dateHelpers';
import { getAverageDeveloperCostFromSource } from '../../utils/companyConfigurations';

const VIEW_CONFIG = {
  developers: {
    groupBy: 'developers',
    singular: 'Developer',
    clearFilters: ['authors'],
    hiddenFilters: [
      'authors',
      'categories',
      'branches',
      'language_tags',
      'viewType'
    ]
  },
  repositories: {
    groupBy: 'repositories',
    singular: 'Repository',
    clearFilters: ['repos'],
    hiddenFilters: [
      'repos',
      'categories',
      'branches',
      'language_tags',
      'viewType'
    ]
  },
  projects: {
    groupBy: 'projects',
    singular: 'Project',
    clearFilters: ['projects', 'repos'],
    hiddenFilters: [
      'projects',
      'repos',
      'categories',
      'branches',
      'language_tags',
      'viewType'
    ]
  }
};

const CHART_CATEGORIES = {
  human: { label: 'Human Effort Required', value: 'human' },
  ai: { label: 'Effort Saved by AI', value: 'ai' }
};

const hasSelectedValues = value =>
  Array.isArray(value) ? value.length > 0 : Boolean(value?.value || value);

const hasScopeFilters = filters =>
  ['authors', 'repos', 'projects', 'tags'].some(key =>
    hasSelectedValues(filters?.[key])
  );

export const getLeverageLabel = ({
  humanEffort = 0,
  aiEffort = 0,
  leverage
}) => {
  if (Number(humanEffort) <= 0 && Number(aiEffort) > 0) return '100% AI';
  if (Number(humanEffort) <= 0 && Number(aiEffort) <= 0) return '—';
  return `${Number(leverage || 1).toFixed(1)}x`;
};

export const convertEffort = (value, operation, averageDeveloperCost) =>
  operation === operations.INVESTMENT
    ? (Number(value || 0) / 60) * Number(averageDeveloperCost || 0)
    : Number(value || 0);

const formatMetric = (value, operation) => {
  const number = Number(value || 0);
  if (operation === operations.INVESTMENT) {
    return number.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    });
  }
  return `${Math.round(number).toLocaleString('en-US')} REV`;
};

export const formatSummaryMetric = (value, operation) => {
  if (operation === operations.INVESTMENT) {
    return formatMetric(value, operation);
  }
  return Math.round(Number(value || 0)).toLocaleString('en-US');
};

const convertTrend = (data, operation, averageDeveloperCost) =>
  Object.fromEntries(
    Object.entries(data || {}).map(([key, row]) => [
      key,
      {
        ...row,
        human: convertEffort(row.human, operation, averageDeveloperCost),
        ai: convertEffort(row.ai, operation, averageDeveloperCost)
      }
    ])
  );

const AiLeverageSummary = ({ summary, filtered, operation, averageCost }) => {
  const human = convertEffort(summary?.humanEffort, operation, averageCost);
  const ai = convertEffort(summary?.aiEffort, operation, averageCost);
  const total = convertEffort(summary?.totalEffort, operation, averageCost);

  return (
    <section className="relative overflow-hidden rounded-2xl border border-slate-200 bg-[radial-gradient(circle_at_top_left,rgba(60,80,224,0.16),transparent_38%),linear-gradient(135deg,#ffffff_0%,#f8fafc_100%)] p-5 shadow-card dark:border-strokedark dark:bg-boxdark sm:p-6">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-violet-500 via-primary to-cyan-400" />
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
            {filtered ? 'Filtered AI Leverage' : 'Overall AI Leverage'}
          </p>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-4xl font-black tracking-tight text-slate-950 dark:text-white">
              {getLeverageLabel(summary || {})}
            </span>
            <TrendingUpIcon className="text-violet-500" />
          </div>
          <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
            AI Leverage compares the baseline effort needed to deliver the same
            output with the human effort actually required. It is not a measure
            of code authorship or AI-generated lines.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {[
            ['Human', human, 'text-sky-600 dark:text-sky-300'],
            ['AI', ai, 'text-violet-600 dark:text-violet-300'],
            ['Total', total, 'text-slate-950 dark:text-white']
          ].map(([label, value, color]) => (
            <div
              key={label}
              className="min-w-0 rounded-xl border border-slate-200 bg-white/80 px-3 py-3 text-right dark:border-slate-700 dark:bg-slate-900/70 sm:min-w-28"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {label}
              </p>
              <p
                className={`mt-1 truncate text-sm font-bold sm:text-base ${color}`}
              >
                {formatSummaryMetric(value, operation)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const LeverageRow = ({
  row,
  maximumComponent,
  operation,
  averageCost,
  onClick
}) => {
  const human = convertEffort(row.humanEffort, operation, averageCost);
  const ai = convertEffort(row.aiEffort, operation, averageCost);
  const total = human + ai;
  const humanWidth = maximumComponent
    ? `${Math.min(100, (Number(row.humanEffort || 0) / maximumComponent) * 100)}%`
    : '0%';
  const aiWidth = maximumComponent
    ? `${Math.min(100, (Number(row.aiEffort || 0) / maximumComponent) * 100)}%`
    : '0%';

  return (
    <button
      type="button"
      onClick={() => onClick(row)}
      className="grid w-full grid-cols-1 gap-3 border-b border-slate-100 px-4 py-4 text-left transition-colors last:border-b-0 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40 dark:border-slate-800 dark:hover:bg-slate-800/50 lg:grid-cols-[minmax(150px,0.7fr)_minmax(300px,2fr)_140px_90px] lg:items-center lg:px-5"
    >
      <span className="truncate text-sm font-semibold text-slate-900 dark:text-white">
        {row.label}
      </span>
      <span>
        <span className="mb-1 grid grid-cols-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 lg:hidden">
          <span>Human</span>
          <span className="text-right">AI Leverage</span>
        </span>
        <span
          className="grid grid-cols-2 items-center gap-1"
          aria-hidden="true"
        >
          <span className="flex h-7 items-center justify-end rounded-l-md bg-sky-50 pr-px dark:bg-sky-950/30">
            <span
              className="h-3.5 rounded-l-full bg-sky-500"
              style={{ width: humanWidth }}
            />
          </span>
          <span className="flex h-7 items-center rounded-r-md bg-violet-50 pl-px dark:bg-violet-950/30">
            <span
              className="h-3.5 rounded-r-full bg-violet-500"
              style={{ width: aiWidth }}
            />
          </span>
        </span>
      </span>
      <span className="text-sm font-bold text-slate-900 dark:text-white lg:text-right">
        {formatMetric(total, operation)}
      </span>
      <span className="w-fit rounded-full bg-violet-100 px-2.5 py-1 text-xs font-bold text-violet-700 dark:bg-violet-500/15 dark:text-violet-300 lg:justify-self-end">
        {getLeverageLabel(row)}
      </span>
      <span className="sr-only">
        Human {formatMetric(human, operation)}, AI {formatMetric(ai, operation)}
      </span>
    </button>
  );
};

const AiLeverageTrendModal = ({
  entity,
  filters,
  operation,
  averageCost,
  onClose
}) => {
  const [period, setPeriod] = useState(periods.MONTH);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!entity) return undefined;
    let active = true;
    setLoading(true);
    sendRequest('/dashboard/ai-leverage/trend', 'POST', {
      ...filters,
      entityType: entity.entityType,
      entityId: entity.id,
      period
    })
      .then(response => {
        if (active) setData(response?.data?.groupedDataByPeriod || {});
      })
      .catch(() => {
        if (active) setData({});
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [entity, filters, period]);

  return (
    <Modal
      isOpen={Boolean(entity)}
      onClose={onClose}
      title={`${entity?.label || ''} AI Leverage Trend`}
      size="xl"
    >
      <SplineChart
        loading={loading}
        dateRange={filters.dateRange}
        categories={CHART_CATEGORIES}
        data={convertTrend(data, operation, averageCost)}
        chartTitle="Human and AI Contribution"
        sortingColumnName="total"
        sendCategoriesSend={() => {}}
        changePeriod={setPeriod}
      />
    </Modal>
  );
};

const AiLeverageDashboard = ({ view }) => {
  const config = VIEW_CONFIG[view] || VIEW_CONFIG.developers;
  const { userInfo = {} } = useSelector(state => state);
  const [filters, setFilters] = useState(null);
  const [data, setData] = useState({ summary: createEmptySummary(), rows: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [fallbackAverageCost, setFallbackAverageCost] = useState(0);
  const averageCost =
    getAverageDeveloperCostFromSource(userInfo) ||
    getAverageDeveloperCostFromSource(userInfo?.user) ||
    fallbackAverageCost;

  useEffect(() => {
    if (averageCost > 0) return undefined;
    let active = true;
    sendRequest('/company-configurations', 'GET')
      .then(response => {
        if (active) {
          setFallbackAverageCost(getAverageDeveloperCostFromSource(response));
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [averageCost]);

  const loadBreakdown = useCallback(async () => {
    if (!filters) return;
    setLoading(true);
    setError('');
    try {
      const response = await sendRequest(
        '/dashboard/ai-leverage/breakdown',
        'POST',
        { ...filters, groupBy: config.groupBy }
      );
      setData(response?.data || { summary: createEmptySummary(), rows: [] });
    } catch (requestError) {
      setData({ summary: createEmptySummary(), rows: [] });
      setError(
        requestError?.message || 'AI leverage data could not be loaded.'
      );
    } finally {
      setLoading(false);
    }
  }, [config.groupBy, filters]);

  useEffect(() => {
    loadBreakdown();
  }, [loadBreakdown]);

  const maximumComponent = useMemo(
    () =>
      Math.max(
        0,
        ...(data.rows || []).flatMap(row => [row.humanEffort, row.aiEffort])
      ),
    [data.rows]
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-3">
        <Filters
          userInfo={userInfo}
          onChange={setFilters}
          isHidden={config.hiddenFilters}
          operationKeys={['GIT_EFFORT', 'INVESTMENT']}
          allowRepoProjectIntersection={true}
          forceClearMultiFilters={config.clearFilters}
        />
      </div>

      {loading ? (
        <>
          <Skeleton className="h-44 w-full rounded-2xl" />
          <Skeleton className="h-80 w-full rounded-2xl" />
        </>
      ) : error ? (
        <EmptyState
          title="AI leverage could not be loaded"
          description={error}
        />
      ) : (
        <>
          <AiLeverageSummary
            summary={data.summary}
            filtered={hasScopeFilters(filters)}
            operation={filters?.operation}
            averageCost={averageCost}
          />
          {(data.rows || []).length === 0 ? (
            <EmptyState
              title={`No ${config.groupBy} found`}
              description="Try a wider date range or clear one of the selected filters."
              icon={<AutoAwesomeIcon />}
            />
          ) : (
            <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-card dark:border-strokedark dark:bg-boxdark">
              <div className="hidden grid-cols-[minmax(150px,0.7fr)_minmax(300px,2fr)_140px_90px] gap-3 border-b border-slate-200 bg-slate-50 px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 lg:grid">
                <span>{config.singular}</span>
                <span className="grid grid-cols-2 gap-2 text-center">
                  <span>Human</span>
                  <span>AI Leverage</span>
                </span>
                <span className="text-right">Total effort</span>
                <span className="text-right">Leverage</span>
              </div>
              {(data.rows || []).map(row => (
                <LeverageRow
                  key={`${row.entityType}-${row.id}`}
                  row={row}
                  maximumComponent={maximumComponent}
                  operation={filters?.operation}
                  averageCost={averageCost}
                  onClick={setSelectedEntity}
                />
              ))}
            </section>
          )}
        </>
      )}

      {selectedEntity && (
        <AiLeverageTrendModal
          entity={selectedEntity}
          filters={filters}
          operation={filters?.operation}
          averageCost={averageCost}
          onClose={() => setSelectedEntity(null)}
        />
      )}
    </div>
  );
};

function createEmptySummary() {
  return { humanEffort: 0, aiEffort: 0, totalEffort: 0, leverage: null };
}

export default AiLeverageDashboard;

export const AiLeverageDevelopers = () => (
  <AiLeverageDashboard view="developers" />
);

export const AiLeverageRepositories = () => (
  <AiLeverageDashboard view="repositories" />
);

export const AiLeverageProjects = () => <AiLeverageDashboard view="projects" />;
